import React, { useRef, useEffect, useState } from 'react';
import { colord } from 'colord';

const useDraggable = (onChange: (x: number, y: number) => void) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleDrag = (e: React.PointerEvent | PointerEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    onChange(x, y);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    handleDrag(e);

    const onPointerMove = (e: PointerEvent) => handleDrag(e);
    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  return { ref, onPointerDown };
};

export const CustomColorPicker = ({ color, onChange }: { color: string, onChange: (hex: string) => void }) => {
  const [hsv, setHsv] = useState(() => colord(color).toHsv());

  // Sync when prop changes
  useEffect(() => {
    const newHsv = colord(color).toHsv();
    setHsv(prev => {
      if (Math.abs(prev.h - newHsv.h) > 1 || Math.abs(prev.s - newHsv.s) > 1 || Math.abs(prev.v - newHsv.v) > 1) {
        return newHsv;
      }
      return prev;
    });
  }, [color]);

  const hueDraggable = useDraggable((x, y) => {
    const dx = x - 0.5;
    const dy = y - 0.5;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);
    let hue = (angle + 90 + 360) % 360;
    setHsv(prev => {
      const next = { ...prev, h: hue };
      onChange(colord(next).toHex());
      return next;
    });
  });

  const boxDraggable = useDraggable((x, y) => {
    setHsv(prev => {
      const next = { ...prev, s: x * 100, v: (1 - y) * 100 };
      onChange(colord(next).toHex());
      return next;
    });
  });

  const sliderDraggable = useDraggable((_x, y) => {
    setHsv(prev => {
      const next = { ...prev, v: (1 - y) * 100 };
      onChange(colord(next).toHex());
      return next;
    });
  });

  const angleRad = (hsv.h - 90) * (Math.PI / 180);
  const hx = 60 + 50 * Math.cos(angleRad);
  const hy = 60 + 50 * Math.sin(angleRad);
  const pureHue = colord({ h: hsv.h, s: 100, v: 100 }).toHex();

  return (
    <div className="mock_color_picker">
      <div
        className="wheel"
        ref={hueDraggable.ref}
        onPointerDown={hueDraggable.onPointerDown}
        style={{ cursor: 'pointer' }}
      >
        <div
          className="wheel_thumb"
          style={{
            position: 'absolute',
            width: 16, height: 16, borderRadius: '50%',
            background: pureHue,
            border: '2px solid #fff',
            left: hx - 8, top: hy - 8,
            pointerEvents: 'none'
          }}
        />

        <div
          className="inner_box"
          ref={boxDraggable.ref}
          onPointerDown={(e) => { e.stopPropagation(); boxDraggable.onPointerDown(e); }}
          style={{
            background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${pureHue})`,
            cursor: 'crosshair',
            position: 'relative',
            borderRadius: '12px',
            overflow: 'hidden'
          }}
        >
          <div
            className="box_thumb"
            style={{
              position: 'absolute',
              width: 12, height: 12, borderRadius: '50%',
              border: '2px solid #fff',
              left: `calc(${hsv.s}% - 6px)`,
              top: `calc(${100 - hsv.v}% - 6px)`,
              pointerEvents: 'none',
              boxShadow: '0 0 2px rgba(0,0,0,0.5)'
            }}
          />
        </div>
      </div>

      <div
        className="slider"
        ref={sliderDraggable.ref}
        onPointerDown={sliderDraggable.onPointerDown}
        style={{
          background: `linear-gradient(to bottom, ${pureHue}, #000000)`,
          cursor: 'pointer'
        }}
      >
        <div
          className="thumb"
          style={{
            top: `calc(${(1 - hsv.v / 100) * 100}% - 8px)`,
            pointerEvents: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}
        />
      </div>
    </div>
  );
};
